/**
 * Fiscal Invoices Page
 * Shows all fiscalized invoices from DevPOS
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  FileCheck, 
  FileX,
  Download,
  Search,
  Filter,
  RefreshCw,
  Mail,
  Eye,
  Printer,
  Calendar,
  User,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FiscalInvoices = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    fiscalized: 0,
    pending: 0,
    failed: 0,
    total_amount: 0
  });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/fiscal-invoices', {
        params: {
          search: searchTerm || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });
      setInvoices(response.data.invoices || []);
      setStats(response.data.stats || stats);
    } catch (error) {
      console.error('Error fetching fiscal invoices:', error);
      toast.error('Gabim në ngarkimin e faturave fiskale');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Load fiscal invoice PDF preview when dialog opens
  useEffect(() => {
    let revoked = false;
    let createdUrl = null;

    const loadPdf = async () => {
      if (!detailsOpen || !selectedInvoice || selectedInvoice.status !== 'fiscalized') {
        return;
      }
      try {
        setPdfPreviewLoading(true);
        setPdfPreviewError(null);
        const safeId = encodeURIComponent(String(selectedInvoice.id ?? ''));
        const response = await fetch(
          `${API_URL}/api/admin/fiscal-invoices/${safeId}/pdf?source=local`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (revoked) return;
        createdUrl = window.URL.createObjectURL(blob);
        setPdfPreviewUrl(createdUrl);
      } catch (err) {
        if (!revoked) {
          setPdfPreviewError('Fatura nuk u ngarkua dot');
        }
      } finally {
        if (!revoked) setPdfPreviewLoading(false);
      }
    };

    loadPdf();

    return () => {
      revoked = true;
      if (createdUrl) {
        window.URL.revokeObjectURL(createdUrl);
      }
      setPdfPreviewUrl(null);
      setPdfPreviewLoading(false);
      setPdfPreviewError(null);
    };
  }, [detailsOpen, selectedInvoice, token]);

  const handleResendEmail = async (invoiceId) => {
    try {
      const safeId = encodeURIComponent(String(invoiceId ?? ''));
      const res = await api.post(`/admin/fiscal-invoices/${safeId}/resend-email`);
      const withPdf = res?.data?.with_pdf;
      toast.success(
        withPdf === false
          ? 'Email-i u dërgua (pa PDF, vetëm me link verifikimi)'
          : 'Email-i u ridërgua me sukses'
      );
      fetchInvoices();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Gabim në ridërgimin e email-it';
      toast.error(msg);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const safeId = encodeURIComponent(String(invoice?.id ?? ''));
      const response = await fetch(`${API_URL}/api/admin/fiscal-invoices/${safeId}/pdf?source=local`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Fatura_Fiskale_${invoice.eic || invoice.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('PDF nuk u gjet ose ka gabim');
      }
    } catch (error) {
      toast.error('Gabim në shkarkimin e PDF-së');
    }
  };

  const handlePrintPdf = async (invoice) => {
    try {
      const safeId = encodeURIComponent(String(invoice?.id ?? ''));
      const response = await fetch(`${API_URL}/api/admin/fiscal-invoices/${safeId}/pdf?source=local`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        toast.error('PDF nuk u gjet ose ka gabim');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (_) {
          toast.error('Printimi dështoi');
        } finally {
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            iframe.remove();
          }, 1500);
        }
      };
    } catch (error) {
      toast.error('Gabim në printimin e PDF-së');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      fiscalized: { label: 'Fiskalizuar', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      pending: { label: 'Në Pritje', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      failed: { label: 'Dështuar', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge variant="outline" className={config.className}>
        {status === 'fiscalized' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
        {status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="fiscal-invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <FileCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Faturat Fiskale</h1>
            <p className="text-sm text-zinc-500">Faturat e fiskalizuara nga DevPOS</p>
          </div>
        </div>
        <Button onClick={fetchInvoices} variant="outline" className="border-zinc-700">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Rifresko
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Totali</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <FileCheck className="w-8 h-8 text-zinc-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Fiskalizuar</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.fiscalized}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Dështuar</p>
                <p className="text-2xl font-bold text-red-400">{stats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Vlera Totale</p>
                <p className="text-2xl font-bold text-[#afd039]">{stats.total_amount?.toLocaleString() || 0} LEK</p>
              </div>
              <CreditCard className="w-8 h-8 text-[#afd039]/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Kërko sipas EIC, emrit ose email-it..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-zinc-700">
                <Filter className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="Statusi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Të Gjitha</SelectItem>
                <SelectItem value="fiscalized">Fiskalizuar</SelectItem>
                <SelectItem value="pending">Në Pritje</SelectItem>
                <SelectItem value="failed">Dështuar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <FileX className="w-12 h-12 mb-4 text-zinc-600" />
              <p>Nuk ka fatura fiskale</p>
              <p className="text-sm text-zinc-600">Faturat do të shfaqen pasi bëhet TopUp me POK Payment</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">EIC</TableHead>
                  <TableHead className="text-zinc-400">Data</TableHead>
                  <TableHead className="text-zinc-400">Klienti</TableHead>
                  <TableHead className="text-zinc-400">ID/NIPT</TableHead>
                  <TableHead className="text-zinc-400">Shuma</TableHead>
                  <TableHead className="text-zinc-400">Statusi</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow 
                    key={invoice.id} 
                    className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setDetailsOpen(true);
                    }}
                  >
                    <TableCell className="font-mono text-emerald-400">
                      {invoice.eic || '-'}
                    </TableCell>
                    <TableCell className="text-zinc-300">
                      {formatDate(invoice.created_at)}
                    </TableCell>
                    <TableCell className="text-white">
                      {invoice.client_name || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-zinc-400">
                      {invoice.client_id_number || '-'}
                    </TableCell>
                    <TableCell className="text-[#afd039] font-medium">
                      {invoice.amount?.toLocaleString()} LEK
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(invoice.status)}
                    </TableCell>
                    <TableCell>
                      {invoice.email_sent ? (
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                          <Mail className="w-3 h-3 mr-1" />
                          Dërguar
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">
                          Jo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {invoice.status === 'fiscalized' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadPdf(invoice)}
                              title="Shkarko PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrintPdf(invoice)}
                              title="Printo PDF"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleResendEmail(invoice.id)}
                              title="Ridërgo Email"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-400" />
              Detajet e Faturës Fiskale
            </DialogTitle>
            <DialogDescription>
              Informacion i plotë për faturën e fiskalizuar
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6 mt-4">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg border ${
                selectedInvoice.status === 'fiscalized' 
                  ? 'bg-emerald-500/10 border-emerald-500/20' 
                  : selectedInvoice.status === 'failed'
                  ? 'bg-red-500/10 border-red-500/20'
                  : 'bg-yellow-500/10 border-yellow-500/20'
              }`}>
                <div className="flex items-center gap-3">
                  {selectedInvoice.status === 'fiscalized' && <CheckCircle className="w-5 h-5 text-emerald-400" />}
                  {selectedInvoice.status === 'failed' && <XCircle className="w-5 h-5 text-red-400" />}
                  {selectedInvoice.status === 'pending' && <Clock className="w-5 h-5 text-yellow-400" />}
                  <div>
                    <p className={`font-medium ${
                      selectedInvoice.status === 'fiscalized' ? 'text-emerald-400' 
                      : selectedInvoice.status === 'failed' ? 'text-red-400'
                      : 'text-yellow-400'
                    }`}>
                      {selectedInvoice.status === 'fiscalized' ? 'Fatura është Fiskalizuar' 
                        : selectedInvoice.status === 'failed' ? 'Fiskalizimi Dështoi'
                        : 'Në Pritje të Fiskalizimit'}
                    </p>
                    {selectedInvoice.eic && (
                      <p className="text-sm text-zinc-400">EIC: <span className="font-mono text-emerald-400">{selectedInvoice.eic}</span></p>
                    )}
                  </div>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">Data Krijimit</p>
                  <p className="text-white">{formatDate(selectedInvoice.created_at)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">Shuma</p>
                  <p className="text-[#afd039] font-bold text-lg">{selectedInvoice.amount?.toLocaleString()} LEK</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">Klienti</p>
                  <p className="text-white">{selectedInvoice.client_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">ID Card / NIPT</p>
                  <p className="text-white font-mono">{selectedInvoice.client_id_number || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">Email Klientit</p>
                  <p className="text-white">{selectedInvoice.client_email || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase">Metoda Pagesës</p>
                  <p className="text-white">{selectedInvoice.payment_method || 'POK Payment'}</p>
                </div>
                {selectedInvoice.order_id && (
                  <div className="space-y-1">
                    <p className="text-xs text-zinc-500 uppercase">Order ID</p>
                    <p className="text-white font-mono text-sm">{selectedInvoice.order_id}</p>
                  </div>
                )}
                {selectedInvoice.invoice_number && (
                  <div className="space-y-1">
                    <p className="text-xs text-zinc-500 uppercase">Numri Faturës</p>
                    <p className="text-white font-mono">{selectedInvoice.invoice_number}</p>
                  </div>
                )}
              </div>

              {/* Email Status */}
              {selectedInvoice.status === 'fiscalized' && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm text-zinc-400">Email me faturë PDF</span>
                    </div>
                    {selectedInvoice.email_sent ? (
                      <Badge className="bg-blue-500/20 text-blue-400">
                        Dërguar {selectedInvoice.email_sent_at ? `- ${formatDate(selectedInvoice.email_sent_at)}` : ''}
                      </Badge>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleResendEmail(selectedInvoice.id)}
                        className="border-zinc-700"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Dërgo
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Fiscal Invoice PDF Preview */}
              {selectedInvoice.status === 'fiscalized' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500 uppercase">Fatura e Fiskalizuar</p>
                    {pdfPreviewUrl && (
                      <a
                        href={pdfPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Hap në tab të ri
                      </a>
                    )}
                  </div>
                  <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                    {pdfPreviewLoading ? (
                      <div className="flex items-center justify-center h-full text-zinc-500">
                        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                        Duke ngarkuar faturën...
                      </div>
                    ) : pdfPreviewError ? (
                      <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                        <FileX className="w-10 h-10 mb-2 text-zinc-600" />
                        <p>{pdfPreviewError}</p>
                      </div>
                    ) : pdfPreviewUrl ? (
                      <iframe
                        title="Fatura Fiskale"
                        src={pdfPreviewUrl}
                        className="w-full h-full"
                        style={{ border: 0 }}
                      />
                    ) : null}
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedInvoice.status === 'fiscalized' && (
                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={() => handleDownloadPdf(selectedInvoice)}
                    className="flex-1 bg-[#afd039] hover:bg-[#9ec02e] text-zinc-900"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Shkarko PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePrintPdf(selectedInvoice)}
                    className="border-zinc-700"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Printo
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleResendEmail(selectedInvoice.id)}
                    className="border-zinc-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Ridërgo Email
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FiscalInvoices;
